import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, SolidButton, StatusIndicator } from '../buttons-visuals';
import { ContentContainer } from '../containers';
import { useProfile } from '../../hooks/store';
import { ProvaPracticeStatus, ProvaPracticeTitleOption, ProvaService } from '../../services/provaService';

import './index.scss';

interface ProvaPracticeProps {
    /** When set, shows that user's attempts (profile). */
    userId?: string;
    /** Offer Start Solo Play (dashboard / league / own profile). */
    showStart?: boolean;
}

interface SoloGameTile {
    id: string;
    name: string;
    imageUrl?: string;
    titles: ProvaPracticeTitleOption[];
}

const formatScore = (attempt: Core.Models.ProvaAttempt): string => {
    if (typeof attempt.competitiveScore === 'number') return attempt.competitiveScore.toFixed(1).replace(/\.0$/, '');
    if (typeof attempt.overallScore === 'number') return attempt.overallScore.toFixed(1).replace(/\.0$/, '');
    return '—';
};

const statusLabel = (status: string): string => {
    switch ((status || '').toLowerCase()) {
        case 'scored':
            return 'Completed';
        case 'launched':
            return 'Ready to play';
        case 'inprogress':
            return 'In progress';
        case 'disqualified':
            return 'Disqualified';
        case 'incomplete':
            return 'Incomplete';
        case 'noshow':
            return 'No show';
        case 'cancelled':
            return 'Cancelled';
        default:
            return status || 'Unknown';
    }
};

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch ((status || '').toLowerCase()) {
        case 'scored':
            return 'success';
        case 'launched':
        case 'inprogress':
            return 'warning';
        case 'disqualified':
        case 'noshow':
        case 'cancelled':
            return 'error';
        default:
            return 'default';
    }
};

const formatWhen = (iso: string): string => {
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const isOpenAttempt = (status: string): boolean =>
    ['launched', 'inprogress'].includes((status || '').toLowerCase());

const ProvaPractice = (props: ProvaPracticeProps): JSX.Element => {
    const { userId, showStart = !userId } = props;
    const profile = useProfile();
    const isSchoolMember = !!profile?.organizationId;

    const [attempts, setAttempts] = useState<Core.Models.ProvaAttempt[]>([]);
    const [status, setStatus] = useState<ProvaPracticeStatus | undefined>();
    const [catalogGames, setCatalogGames] = useState<Core.Models.ProvaCatalogGame[]>([]);
    const [loading, setLoading] = useState(true);
    const [startingKey, setStartingKey] = useState<string | undefined>();
    const [selectedGameId, setSelectedGameId] = useState<string | undefined>();

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [attemptData, statusData, gamesData] = await Promise.all([
                userId ? ProvaService.listAttemptsForUser(userId) : ProvaService.listMyAttempts(),
                showStart ? ProvaService.getStatus() : Promise.resolve(undefined),
                showStart ? ProvaService.getCatalogGames().catch(() => [] as Core.Models.ProvaCatalogGame[]) : Promise.resolve([]),
            ]);
            setAttempts(attemptData);
            setCatalogGames(gamesData);
            if (statusData) {
                setStatus({
                    ...statusData,
                    titles: statusData.titles ?? [],
                });
            }
        } catch (e) {
            if (showStart) toast.error(Core.API.getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [showStart, userId]);

    useEffect(() => {
        void load();
    }, [load]);

    const titles = status?.titles ?? [];
    const titleNameByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of titles) map.set(t.titleKey.toLowerCase(), t.name);
        return map;
    }, [titles]);

    const games = useMemo((): SoloGameTile[] => {
        const byId = new Map<string, SoloGameTile>();

        for (const game of catalogGames) {
            byId.set(game.id, {
                id: game.id,
                name: game.name,
                imageUrl: game.cardUrl || game.iconUrl,
                titles: [],
            });
        }

        for (const title of titles) {
            const id = title.gameId || title.gameName || title.titleKey;
            const existing = byId.get(id);
            if (existing) {
                existing.titles.push(title);
            } else {
                byId.set(id, {
                    id,
                    name: title.gameName || 'Solo Play',
                    titles: [title],
                });
            }
        }

        return [...byId.values()]
            .filter((game) => game.titles.length > 0 || !showStart)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [catalogGames, showStart, titles]);

    const selectedGame = useMemo(
        () => games.find((game) => game.id === selectedGameId),
        [games, selectedGameId]
    );

    const canStart = useMemo(
        () => showStart && isSchoolMember && !!status?.canStart && titles.length > 0,
        [isSchoolMember, showStart, status?.canStart, titles.length]
    );

    const start = useCallback(
        async (titleKey: string) => {
            if (startingKey || !canStart || !titleKey) return;
            setStartingKey(titleKey);
            try {
                const attempt = await ProvaService.startAttempt(titleKey, window.location.href);
                if (attempt.joinUrl) {
                    window.open(attempt.joinUrl, '_blank', 'noopener,noreferrer');
                    toast.success('Solo Play opened in a new tab. Your score will appear here when you finish.');
                } else {
                    toast.error('Solo Play started but no join link was returned.');
                }
                await load();
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
            } finally {
                setStartingKey(undefined);
            }
        },
        [canStart, load, startingKey]
    );

    const heading = showStart ? 'Solo Play' : 'Solo Play scores';
    const lede = showStart
        ? selectedGame
            ? `Choose a challenge in ${selectedGame.name}. When you finish, your score shows up here and on your profile.`
            : status?.message ||
              'Pick a game, then choose a challenge. When you finish, your score shows up here and on your profile.'
        : 'Scores from completed Solo Play sessions.';

    const emptyMessage = (() => {
        if (showStart && status && !status.available) return status.message;
        if (showStart && !isSchoolMember) return 'Join your school on LeagueSpot to start Solo Play.';
        if (showStart && titles.length === 0) {
            return status?.message || 'Solo Play challenges will appear here once they are available.';
        }
        if (showStart) return 'No Solo Play sessions yet. Pick a game when you are ready.';
        return 'No Solo Play scores yet.';
    })();

    const resolveAttemptName = (titleKey?: string) =>
        (titleKey && titleNameByKey.get(titleKey.toLowerCase())) || titleKey || 'Solo Play session';

    return (
        <div className="prova-practice-wrap">
            <ContentContainer
                className="prova-practice shadow-medium p0"
                shade={Core.Models.Shades.Dark20}
                radius={Core.Models.RadiusSizes.Large}
            >
                <div className="prova-practice__header">
                    <div className="prova-practice__heading-block">
                        {selectedGame && canStart && (
                            <HollowButton as="button" size="small" onClick={() => setSelectedGameId(undefined)}>
                                All games
                            </HollowButton>
                        )}
                        <h2 className="heading-2 mb0">{selectedGame && canStart ? selectedGame.name : heading}</h2>
                    </div>
                </div>

                <div className="prova-practice__body">
                    {showStart && <p className="prova-practice__lede">{lede}</p>}

                    {canStart && !selectedGame && games.length > 0 && (
                        <div className="prova-practice__game-grid" role="list">
                            {games.map((game) => (
                                <button
                                    key={game.id}
                                    type="button"
                                    className="prova-practice__game-tile"
                                    role="listitem"
                                    onClick={() => setSelectedGameId(game.id)}
                                >
                                    {game.imageUrl ? (
                                        <img src={game.imageUrl} alt="" />
                                    ) : (
                                        <span className="prova-practice__game-tile-fallback" aria-hidden />
                                    )}
                                    <span className="prova-practice__game-tile-shade" />
                                    <span className="prova-practice__game-tile-meta">
                                        <span className="prova-practice__game-tile-name">{game.name}</span>
                                        <span className="prova-practice__game-tile-count">
                                            {game.titles.length} challenge{game.titles.length === 1 ? '' : 's'}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {canStart && selectedGame && (
                        <ul className="prova-practice__challenges">
                            {selectedGame.titles.map((title) => (
                                <li key={title.titleKey} className="prova-practice__challenge">
                                    <span className="prova-practice__challenge-name">{title.name}</span>
                                    <SolidButton
                                        as="button"
                                        size="small"
                                        attention
                                        onClick={() => void start(title.titleKey)}
                                        pending={startingKey === title.titleKey}
                                    >
                                        Play
                                    </SolidButton>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="prova-practice__scores">
                        <h3 className="prova-practice__scores-title heading-3">Recent scores</h3>
                        {loading ? (
                            <p className="prova-practice__empty">Loading Solo Play…</p>
                        ) : attempts.length === 0 ? (
                            <div className="prova-practice__empty-state">
                                <p className="prova-practice__empty">{emptyMessage}</p>
                            </div>
                        ) : (
                            <ul className="prova-practice__list">
                                {attempts.map((attempt) => (
                                    <li key={attempt.id} className="prova-practice__row">
                                        <div className="prova-practice__row-main">
                                            <span className="prova-practice__title">
                                                {resolveAttemptName(attempt.titleKey)}
                                            </span>
                                            <span className="prova-practice__date">
                                                {formatWhen(attempt.completedAtUtc || attempt.createdUtc)}
                                            </span>
                                        </div>
                                        <StatusIndicator color={statusColor(attempt.status)} outlineOnly>
                                            {statusLabel(attempt.status)}
                                        </StatusIndicator>
                                        <strong className="prova-practice__score">{formatScore(attempt)}</strong>
                                        {showStart && attempt.joinUrl && isOpenAttempt(attempt.status) && (
                                            <div className="prova-practice__actions">
                                                <HollowButton
                                                    as="a"
                                                    href={attempt.joinUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                >
                                                    Resume
                                                </HollowButton>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </ContentContainer>
        </div>
    );
};

export default ProvaPractice;
